import {
  type ToolInput,
  type ToolName,
  type ToolOutput,
  type ToolResponse,
  toolResponseSchema,
  toolSpecs,
} from '@hugo/core'
import { apiErrorFrom, authHeaders } from './api'
import { apiUrl, isNative } from './platform'

export type ToolCall<N extends ToolName> = { result: ToolOutput<N>; action: ToolResponse['action'] }

export type ToolCallOptions = {
  /** Same key → same result (safe retries). Defaults to a fresh key per call. */
  idempotencyKey?: string
  via?: 'ui' | 'palette' | 'voice'
}

/**
 * Call a tool from the registry. Every change the UI makes goes through here, so the web app,
 * the palette and (later) voice and the agent share one path with validation, undo and audit.
 */
export async function callTool<N extends ToolName>(
  name: N,
  input: ToolInput<N>,
  options: ToolCallOptions = {},
): Promise<ToolCall<N>> {
  const spec = toolSpecs[name]
  const response = await fetch(apiUrl(`/api/tools/${name}`), {
    method: 'POST',
    credentials: isNative ? 'omit' : 'same-origin',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'x-hugo-via': options.via ?? 'ui',
      ...(spec.effect === 'read'
        ? {}
        : { 'idempotency-key': options.idempotencyKey ?? crypto.randomUUID() }),
      ...(await authHeaders()),
    },
    body: JSON.stringify(input ?? {}),
  })
  const body: unknown = await response.json().catch(() => null)
  if (!response.ok) throw apiErrorFrom(response.status, body)
  const envelope = toolResponseSchema.parse(body)
  return { result: spec.output.parse(envelope.result) as ToolOutput<N>, action: envelope.action }
}
